// src/hooks/useFleetData.ts
//
// React Query hooks for all vehicle request data.
// Replaces raw useEffect + useState fetching across:
//   - VehicleRequestsPage
//   - VehicleCalendarScheduler
//   - ApprovalDialogs (driver list)
//
// Cache strategy:
//   - staleTime: how long data is considered fresh (no refetch)
//   - gcTime:    how long unused data stays in memory
//   - Mutations invalidate relevant keys so lists refresh automatically

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vehicleRequestAPI, vehicleAPI, driverAPI, calendarAPI } from '../services/api';
import type { VehicleRequest, FleetVehicle } from '../types';

// ── Query key factory ────────────────────────────────────────────────────────
// Centralised so invalidation is consistent across components.
export const QK = {
  allRequests:     (params?: object)         => ['vehicleRequests', 'all', params]        as const,
  pendingSuper:    ()                         => ['vehicleRequests', 'pendingSuper']       as const,
  pendingFleet:    ()                         => ['vehicleRequests', 'pendingFleet']       as const,
  requestStats:    (period: string)           => ['vehicleRequests', 'stats', period]      as const,
  availableVehicles: (start: string, end: string, cat?: string) =>
                                               ['vehicles', 'available', start, end, cat] as const,
  allVehicles:     (params?: object)         => ['vehicles', 'all', params]               as const,
  allDrivers:      (params?: object)         => ['drivers', 'all', params]                as const,
  calendarView:    (start: string, end: string) => ['calendar', 'view', start, end]       as const,
  calendarConflicts: (start: string, end: string) => ['calendar', 'conflicts', start, end] as const,
  peakUsage:       (start: string, end: string) => ['calendar', 'peakUsage', start, end]  as const,
};


// ════════════════════════════════════════════════════════════════════════════
//  VEHICLE REQUESTS
// ════════════════════════════════════════════════════════════════════════════

/** All requests — used in the history tab */
export const useAllRequests = (params?: {
  status?: 'pending' | 'supervisor_approved' | 'fleet_approved' | 'rejected' | 'completed' | 'cancelled' | 'in_progress';
  requestedBy?: string;
  vehicleCategory?: 'executive' | 'staff_bus' | 'technical' | 'general' | 'any';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) =>
  useQuery({
    queryKey:  QK.allRequests(params),
    queryFn:   () => vehicleRequestAPI.getAllRequests({ page: 1, limit: 200, ...params }),
    staleTime: 2 * 60 * 1000,   // 2 min — requests change fairly often
    select:    (res) => res.data as VehicleRequest[],
  });

/** Pending supervisor approval queue */
export const usePendingSupervisor = (enabled = true) =>
  useQuery({
    queryKey:  QK.pendingSuper(),
    queryFn:   vehicleRequestAPI.getPendingSupervisorRequests,
    staleTime: 60 * 1000,        // 1 min — approval queues need to be fresh
    enabled,
  });

/** Pending fleet manager approval queue */
export const usePendingFleet = (enabled = true) =>
  useQuery({
    queryKey:  QK.pendingFleet(),
    queryFn:   vehicleRequestAPI.getPendingFleetRequests,
    staleTime: 60 * 1000,
    enabled,
  });

/** Request stats (30d/7d/90d) */
export const useRequestStats = (period: '7d' | '30d' | '90d' = '30d') =>
  useQuery({
    queryKey:  QK.requestStats(period),
    queryFn:   () => vehicleRequestAPI.getRequestStats(period),
    staleTime: 5 * 60 * 1000,   // 5 min — stats don't need to be real-time
  });

/** Available vehicles for a specific time slot — used in FleetApprovalDialog */
export const useAvailableVehicles = (
  startDateTime: string,
  endDateTime: string,
  category?: string,
  enabled = true
) =>
  useQuery({
    queryKey:  QK.availableVehicles(startDateTime, endDateTime, category),
    queryFn:   () => vehicleRequestAPI.getAvailableVehicles(startDateTime, endDateTime, category),
    staleTime: 2 * 60 * 1000,
    enabled:   enabled && !!startDateTime && !!endDateTime,
  });


// ════════════════════════════════════════════════════════════════════════════
//  VEHICLES
// ════════════════════════════════════════════════════════════════════════════

/** All vehicles (or filtered) — used in stats card and calendar */
export const useAllVehicles = (params?: {
  status?: 'available' | 'in_use' | 'maintenance' | 'repair' | 'decommissioned';
  category?: 'executive' | 'staff_bus' | 'technical' | 'general';
}) =>
  useQuery({
    queryKey:  QK.allVehicles(params),
    queryFn:   () => vehicleAPI.getAllVehicles(params),
    staleTime: 5 * 60 * 1000,   // 5 min — vehicle list is stable
  });


// ════════════════════════════════════════════════════════════════════════════
//  DRIVERS
// ════════════════════════════════════════════════════════════════════════════

/** Active drivers — used in FleetApprovalDialog dropdown */
export const useActiveDrivers = (enabled = true) =>
  useQuery({
    queryKey:  QK.allDrivers({ status: 'active' }),
    queryFn:   () => driverAPI.getAllDrivers({ status: 'active' }),
    staleTime: 10 * 60 * 1000,  // 10 min — driver list changes rarely
    enabled,
  });


// ════════════════════════════════════════════════════════════════════════════
//  CALENDAR
// ════════════════════════════════════════════════════════════════════════════

/** Calendar events for a date range */
export const useCalendarView = (startISO: string, endISO: string) =>
  useQuery({
    queryKey:  QK.calendarView(startISO, endISO),
    queryFn:   () => vehicleRequestAPI.getCalendarView(startISO, endISO),
    staleTime: 2 * 60 * 1000,
    enabled:   !!startISO && !!endISO,
  });

/** Conflict check for a time range */
export const useCalendarConflicts = (startISO: string, endISO: string, enabled = true) =>
  useQuery({
    queryKey:  QK.calendarConflicts(startISO, endISO),
    queryFn:   () => calendarAPI.checkConflicts(startISO, endISO),
    staleTime: 2 * 60 * 1000,
    enabled:   enabled && !!startISO && !!endISO,
  });

/** Peak usage analysis */
export const usePeakUsage = (startISO: string, endISO: string) =>
  useQuery({
    queryKey:  QK.peakUsage(startISO, endISO),
    queryFn:   () => calendarAPI.getPeakUsageAnalysis(startISO, endISO),
    staleTime: 15 * 60 * 1000,  // 15 min — historical data, very stable
    enabled:   !!startISO && !!endISO,
  });


// ════════════════════════════════════════════════════════════════════════════
//  MUTATIONS — invalidate the right cache keys on success
// ════════════════════════════════════════════════════════════════════════════

/** Approve at supervisor level → invalidates request lists */
export const useSupervisorApprove = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, comments }: { requestId: string; comments: string }) =>
      vehicleRequestAPI.supervisorApprove(requestId, comments),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicleRequests'] });
    },
  });
};

/** Reject at supervisor level */
export const useSupervisorReject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason: string }) =>
      vehicleRequestAPI.supervisorReject(requestId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicleRequests'] });
    },
  });
};

/** Approve at fleet level (assigns vehicle + driver) */
export const useFleetApprove = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId, vehicleId, driverName, driverPhone, comments
    }: {
      requestId:   string;
      vehicleId:   string;
      driverName:  string;
      driverPhone: string | null;
      comments:    string;
    }) => vehicleRequestAPI.fleetApprove(requestId, vehicleId, driverName, driverPhone, comments),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicleRequests'] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });       // vehicle schedule changes
      qc.invalidateQueries({ queryKey: ['calendar'] });       // calendar events change
    },
  });
};

/** Reject at fleet level */
export const useFleetReject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason: string }) =>
      vehicleRequestAPI.fleetReject(requestId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicleRequests'] });
    },
  });
};

/** Submit a new internal request */
export const useSubmitRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof vehicleRequestAPI.createRequest>[0]) =>
      vehicleRequestAPI.createRequest(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicleRequests'] });
      qc.invalidateQueries({ queryKey: ['calendar'] });
    },
  });
};

export const useCompleteTrip = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, completionData }: {
      requestId: string;
      completionData?: Parameters<typeof vehicleRequestAPI.completeTrip>[1];
    }) => vehicleRequestAPI.completeTrip(requestId, completionData || {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicleRequests'] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });
};

export const useCancelTrip = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      vehicleRequestAPI.cancelTrip(requestId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicleRequests'] });
    },
  });
};